import { Link } from 'react-router-dom';
import { Symbol } from '../../components/symbol';
import { Skeleton } from '../../components/skeleton';
import { useAttention, type AttentionItem } from '../../services/attention';

// "Needs your attention" — the queues waiting on this admin (approvals,
// withdrawals, refunds, tickets, locked deliveries), each a link straight to
// the page and tab where it's handled. Only queues the admin can act on are
// returned by the API; empty ones are left out.

const TONE: Record<AttentionItem['tone'], { badge: string; icon: string }> = {
  danger: { badge: 'badge-danger', icon: 'error' },
  warning: { badge: 'badge-warning', icon: 'pending_actions' },
  info: { badge: 'badge-info', icon: 'info' },
};

// Most urgent first: things that block someone right now, then approvals.
const ORDER: Record<AttentionItem['tone'], number> = { danger: 0, warning: 1, info: 2 };

export function AttentionPanel() {
  const { data, isLoading, isError } = useAttention();

  if (isLoading) return <Skeleton className="h-[88px] !rounded-lg" />;
  // A failed count must not take the dashboard down with it.
  if (isError || !data) return null;

  const open = data.items.filter((i) => i.count > 0).sort((a, b) => ORDER[a.tone] - ORDER[b.tone] || b.count - a.count);

  if (open.length === 0) {
    return (
      <div className="panel flex items-center gap-3 px-5 py-4">
        <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-success-light text-success-ink">
          <Symbol name="task_alt" size={20} />
        </span>
        <div>
          <p className="text-[14.5px] font-semibold text-ink-900">All caught up</p>
          <p className="text-[13px] text-ink-500">Nothing is waiting for your approval right now.</p>
        </div>
      </div>
    );
  }

  return (
    <section className="panel" aria-labelledby="attention-title">
      <div className="flex items-center justify-between gap-3 border-b border-surface-line px-5 py-4">
        <div className="flex items-center gap-2.5">
          <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-admin-soft text-admin-ink">
            <Symbol name="notifications_active" size={20} />
          </span>
          <div>
            <h2 id="attention-title" className="text-[16px] font-semibold text-ink-900">Needs your attention</h2>
            <p className="text-[13px] text-ink-500">
              {data.total} {data.total === 1 ? 'item is' : 'items are'} waiting · click one to open it
            </p>
          </div>
        </div>
      </div>
      <ul className="grid grid-cols-1 divide-y divide-surface-line md:grid-cols-2 md:divide-y-0">
        {open.map((item) => (
          <li key={item.key} className="md:border-b md:border-surface-line md:odd:border-r">
            <Link
              to={item.href}
              className="group flex items-start gap-3 px-5 py-3.5 transition-colors duration-micro hover:bg-surface-sunk"
            >
              <Symbol
                name={TONE[item.tone].icon}
                size={20}
                className={item.tone === 'danger' ? 'mt-0.5 text-danger-ink' : item.tone === 'warning' ? 'mt-0.5 text-warning-ink' : 'mt-0.5 text-info-ink'}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[14.5px] font-semibold text-ink-900">{item.label}</p>
                <p className="text-[13px] text-ink-500">{item.hint}</p>
              </div>
              <span className={`badge ${TONE[item.tone].badge} font-tabular`}>{item.count}</span>
              <Symbol
                name="arrow_forward"
                size={18}
                className="mt-0.5 text-ink-400 transition-colors group-hover:text-admin-ink"
              />
            </Link>
            {/* The specific records, when the list page can't filter to them
                (e.g. locked deliveries) — each opens that order. */}
            {item.links && item.links.length > 0 && (
              <div className="-mt-1.5 flex flex-wrap gap-1.5 pb-3 pl-[52px] pr-5">
                {item.links.map((l) => (
                  <Link
                    key={l.href}
                    to={l.href}
                    className="sku rounded-xs bg-surface-2 px-1.5 py-0.5 text-[12px] text-ink-700 hover:text-admin-ink"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
